import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { translate } from "../../../config/translator";
import { selectLanguageOrDefault } from "../../../selectors";
import { useStableId } from "../../../hooks/useStableId";
import { LanguageLocaleEnum } from "../../../client/api";
import LoadingSpinner from "../LoadingSpinner";

export type DropdownItem<T extends string | number> = {
  key: T;
  label: string;
};

export function useAnchoredOverlay(
  rect: DOMRect | false,
  setRect: (r: DOMRect | false) => void,
  dropdownId?: string
) {
  useEffect(() => {
    const handleDocClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const triggerSelector = dropdownId ? `#${dropdownId}` : "#clava-dropdown";
      const inTrigger = !!target.closest(triggerSelector);
      const inOverlay = !!target.closest("#clava-dropdown-overlay");
      if (!inTrigger && !inOverlay) setRect(false);
    };
    document.addEventListener("click", handleDocClick, true);
    return () => document.removeEventListener("click", handleDocClick, true);
  }, [setRect, dropdownId]);

  useEffect(() => {
    if (!rect) {
      return undefined;
    }

    const handleScrollOrResize = (e: Event) => {
      const { type, target } = e;
      if (type === "scroll") {
        if (target && typeof target === "object" && "closest" in target) {
          const element = target as HTMLElement;
          if (element.closest("#clava-dropdown-overlay")) {
            return;
          }
        }
      }
      setRect(false);
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize, true);

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize, true);
    };
  }, [rect, setRect]);
}

export function useDropdownVisuals<T extends string | number>({
  l,
  placeholder,
  withoutChoose,
  transparent,
  disabled,
  value,
  primary,
  iconL,
  className,
  items,
}: {
  l: LanguageLocaleEnum;
  placeholder?: string;
  withoutChoose?: boolean;
  transparent?: boolean;
  disabled?: boolean;
  value?: T;
  primary?: boolean;
  iconL?: FontAwesomeIconProps["icon"];
  className?: string;
  items: DropdownItem<T>[];
}) {
  const realPlaceholder = useMemo(() => {
    if (!placeholder) return withoutChoose ? "" : translate("select", l);
    return placeholder;
  }, [l, placeholder, withoutChoose]);

  const inputClass = useMemo(() => {
    let style = "w-full outline-0 rounded-xl";
    if (!transparent) {
      style +=
        " active:bg-input-bg-active active:dark:bg-input-bg-active-dark focus:bg-input-bg-active focus:dark:bg-input-bg-active-dark border active:dark:border-input-border-active-dark focus:dark:border-input-border-active-dark py-2";
      if (disabled) {
        style += " border-input-border opacity-60";
      } else {
        style +=
          " bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark active:border-input-border-active focus:border-input-border-active";
      }
    }
    if (value !== undefined && value !== null) {
      if (primary) style += " !text-primary";
      else style += " text-font dark:text-font-dark";
    } else {
      style += " text-placeholder dark:text-placeholder-dark";
    }
    if (iconL) style += " px-8";
    else style += " pl-2 pr-8";
    return className ? `${style} ${className}` : style;
  }, [transparent, disabled, value, primary, iconL, className]);

  const realValue = useMemo(
    () => items.find((it) => it.key === value)?.label,
    [items, value]
  );

  return { realPlaceholder, inputClass, realValue } as const;
}

type ClavaDropdownProps<T extends string | number> = {
  onChange: (val: T | undefined) => void;
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  withSearch?: boolean;
  onChangeSearchText?: (searchText: string) => void;
  withoutChoose?: boolean;
  label?: string;
  iconL?: FontAwesomeIconProps["icon"];
  items: DropdownItem<T>[];
  transparent?: boolean;
  loading?: boolean;
  primary?: boolean;
  className?: string;
  containerClassName?: string;
  autoOpen?: boolean;
};

function ClavaDropdown<T extends string | number>({
  onChange,
  value,
  placeholder,
  disabled,
  withSearch,
  onChangeSearchText,
  withoutChoose,
  label,
  primary,
  iconL,
  transparent,
  loading,
  items,
  className,
  containerClassName,
  autoOpen,
}: ClavaDropdownProps<T>) {
  const l = useSelector(selectLanguageOrDefault);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState<DOMRect | false>(false);
  const dropdownId = useStableId("clava-dropdown");
  const { realPlaceholder, inputClass, realValue } = useDropdownVisuals({
    l,
    placeholder,
    withoutChoose,
    transparent,
    disabled,
    value,
    primary,
    iconL,
    className,
    items,
  });

  const onOpen = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      if (rect) {
        setRect(false);
        return;
      }
      const clicked = e.target as HTMLElement;
      const container =
        (clicked.closest(`#${dropdownId}`) as HTMLElement) ||
        containerRef.current;
      if (!container) return;
      setRect(container.getBoundingClientRect());
      if (withSearch && inputRef.current) {
        inputRef.current.focus();
      }
    },
    [disabled, rect, withSearch, dropdownId]
  );

  useEffect(() => {
    if (autoOpen && !rect) {
      const el = containerRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(r as DOMRect);
      }
    }
  }, [autoOpen, rect]);

  useAnchoredOverlay(rect, setRect, dropdownId);

  const overlayStyle = useMemo((): React.CSSProperties | null => {
    if (!rect) return null;

    return {
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      position: "fixed",
    };
  }, [rect]);

  const dropdownOverlay = useMemo(() => {
    if (!rect || !overlayStyle) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Close dropdown"
          className="fixed inset-0 z-40 backdrop-blur backdrop-brightness-90"
          onClick={() => setRect(false)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" ||
              e.key === " " ||
              e.key === "Escape"
            ) {
              setRect(false);
            }
          }}
        />
        {/* Dropdown content */}
        <div
          id="clava-dropdown-overlay"
          className="fixed z-50 max-h-96 overflow-y-auto rounded-xl shadow-shadow dark:shadow-shadow-dark drop-shadow-lg bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark"
          style={overlayStyle}
        >
          {!withoutChoose && (
            <button
              type="button"
              className={`px-4 min-w-full py-2 text-left hover:bg-primary-20 text-xs text-font dark:text-font-dark ${
                value === undefined ? "!bg-primary !text-white" : ""
              }`}
              onClick={() => {
                onChange(undefined);
                setRect(false);
              }}
            >
              {translate("options", l)}
            </button>
          )}
          {items.map((item) => (
            <button
              key={`item-${String(item.key)}`}
              type="button"
              className={`px-4 min-w-full py-2 text-left hover:bg-primary-20 text-xs text-font dark:text-font-dark ${
                value === item.key ? "!bg-primary !text-white" : ""
              }`}
              onClick={() => {
                onChange(item.key);
                setRect(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </>
    );
  }, [
    rect,
    overlayStyle,
    withoutChoose,
    value,
    items,
    onChange,
    l,
    setRect,
  ]);

  const onChangeText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQ(event.target.value);
      if (onChangeSearchText) onChangeSearchText(event.target.value);
    },
    [onChangeSearchText]
  );

  return (
    <>
      <div
        className={`relative flex flex-col items-start justify-stretch w-full${
          containerClassName ? ` ${containerClassName}` : ""
        }`}
        id={dropdownId}
        ref={containerRef}
      >
        {!!label && <span className="font-semibold text-xs mb-1">{label}</span>}
        <div className="relative w-full">
          {withSearch ? (
            <input
              ref={inputRef}
              onClick={onOpen}
              className={inputClass}
              value={!rect ? realValue ?? realPlaceholder : q}
              onChange={onChangeText}
            />
          ) : (
            <button
              onClick={onOpen}
              className={inputClass}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={!!rect}
            >
              {realValue ?? realPlaceholder}
            </button>
          )}
          {loading ? (
            <div className="absolute right-3 inset-y-0 flex items-center">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            <button
              type="button"
              onClick={onOpen}
              aria-label={realPlaceholder}
              className="absolute right-3 inset-y-0 flex items-center"
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`w-4 h-4 ${
                  primary ? "!text-primary" : "text-font dark:text-font-dark"
                }`}
              />
            </button>
          )}
          {!!iconL && (
            <span className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
              <FontAwesomeIcon
                icon={iconL}
                className="w-4 h-4 text-font dark:text-font-dark"
              />
            </span>
          )}
        </div>
      </div>
      {typeof document !== "undefined" &&
        createPortal(dropdownOverlay, document.body)}
    </>
  );
}

ClavaDropdown.defaultProps = {
  disabled: undefined,
  label: undefined,
  iconL: undefined,
  placeholder: undefined,
  withSearch: undefined,
  onChangeSearchText: undefined,
  withoutChoose: undefined,
  value: undefined,
  loading: undefined,
  primary: undefined,
  transparent: undefined,
};

export default ClavaDropdown;

